import type {
  DataDumpAggregateType,
  PnlChartSectionType,
  PnlLoss,
  PnlLosses,
  PnlLossTag,
} from "../../../types/types";
import { PnlCategoryLabelsEnum } from "../../../types/enums";
import {
  PNL_COLORS,
  SORTED_STANDARD_PNL_CATEGORIES,
  QUALITY_LOSS_CATEGORY,
  PRODUCING_CHART_TITLES,
} from "../../../constants/constants";
import { CUSTOM_PNL_CHART_HEADER_TITLES } from "constants/customers";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { useCallback } from "react";
import { useDoesCustomerShowProducing } from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { TopDownOrBottomUp } from "components/DataGetter/FlightConfig/FlightConfigTypes/GeneralConfigType";

type TagsObjectForDataDumpPnlRows = {
  [pnlCategory: string]: {
    categoryTime: number;
    categoryPercent: number;
    bars: {
      title: string;
      category: string;
      count: null;
      color: string;
      time: number;
      percent: number;
      percentWidth: number;
    }[];
  };
};

const EMPTY_PNL_ROW = {
  categoryPercent: 0,
  categoryTime: 0,
  bars: [],
};
const getTagsObjectBase = (
  doesCustomerShowProducing
): TagsObjectForDataDumpPnlRows => ({
  producing: EMPTY_PNL_ROW,
  planned: EMPTY_PNL_ROW,
  unplanned: EMPTY_PNL_ROW,
  waiting: EMPTY_PNL_ROW,
  unknown: EMPTY_PNL_ROW,
  notScheduled: EMPTY_PNL_ROW,
  uncollected: EMPTY_PNL_ROW,
  // if customer shows producing, quality goes in producing category, otherwise quality is its own category
  ...(doesCustomerShowProducing ? {} : { quality: EMPTY_PNL_ROW }),
});

export type PnlChartTagBarType = {
  title: string;
  category: string;
  count: number | null;
  color: any;
  time: any;
  percent: number | null;
  percentWidth: any;
};

export function useCalcPnlChartCategoriesWithProducingLosses(): (
  dataDumpWithProducingLosses: DataDumpAggregateType
) => PnlChartSectionType[] {
  const doesCustomerShowProducing = useDoesCustomerShowProducing();
  const { hasWipIncrease } = useFlagsWithDefaults();
  const [
    {
      general: {
        hideProducingLosses,
        includeDependentLosses,
        topDownOrBottomUp,
        producingTimeFromDataDumpAggregate,
      },
    },
  ] = useFlightConfig();
  const ckey = useCustomerKey();
  return useCallback(
    (dataDumpWithProducingLosses: DataDumpAggregateType) =>
      calcPnlChartCategoriesWithProducingLosses(
        dataDumpWithProducingLosses,
        doesCustomerShowProducing,
        hasWipIncrease,
        ckey,
        topDownOrBottomUp,
        hideProducingLosses,
        includeDependentLosses,
        producingTimeFromDataDumpAggregate
      ),
    [
      ckey,
      doesCustomerShowProducing,
      topDownOrBottomUp,
      hasWipIncrease,
      hideProducingLosses,
      includeDependentLosses,
      producingTimeFromDataDumpAggregate,
    ]
  );
}

/**
 * Returns an array of rows (PnlChartSectionType) describing each P&L category.
 *
 * This version of the calcPnlCharts function uses the data dump aggregate API
 * and returns "producing losses" (slowdown, rejects, minor stops)
 */
const calcPnlChartCategoriesWithProducingLosses = (
  dataDumpWithProducingLosses: DataDumpAggregateType,
  /** If customer shows producing, quality goes in producing category, otherwise quality is its own category */
  doesCustomerShowProducing: boolean,
  hasWipIncrease: boolean,
  ckey: string,
  topDownOrBottomUp: TopDownOrBottomUp,
  hideProducingLosses: boolean,
  includeDependentLosses: boolean,
  producingTimeFromDataDumpAggregate: boolean
  // tslint:disable-next-line: no-big-function
): PnlChartSectionType[] => {
  const {
    processIds,
    // netOutput,
    // oeePercent,
    // effectiveUptimePercent,
    // availabilityPercent,
    // teepPercent,
    productionTime,
    // uptimePercent,
    // processNames,
    // speedLossTime,
    // effectiveProducingTimeBottomUp,
    // netOutput,
    // grossOutput,
    // rejectOutput,
    // producingTime,
    range,
    minorStopTime,
    // slowdownLossTime,
    slowdownLossTimeReal,
    // producingTime,
    // rejectLossTime,
    // plannedTime,
    // waitingTime,
    // unplannedTime,
    // unknownTime,
    // notScheduledTime,
    uncollectedTime,
    pnlLosses,
  } = dataDumpWithProducingLosses;

  // slowdownLossTimeReal value coming from Midgard is a more accurate value for slowdown loss time
  // If will eventually have a different, more clear name from midgard, but for now we shall coerce
  const slowdownLossTime = slowdownLossTimeReal;

  const {
    downtimePlusDependentSpeedLossMs: unknownDowntimePlusDependentSpeedLossMs,
    independentSlowdownLossMs: unknownIndependentSlowdownLossMs,
    independentMinorStopLossMs: unknownIndependentMinorStopLossMs,
    downtimeLossMs: unknownDowntimeLossMs,
    allocatableTimeMs: totalUnknownTime,
  } = pnlLosses?.unknown || {};

  const qualityLossTime = pnlLosses?.quality.qualityLossMs;

  const rangeLt = Math.min(Date.now(), range.lt);

  const tagsAffectingBarWidthsArr = [
    ...(pnlLosses?.planned.tags || []),
    ...(pnlLosses?.unplanned.tags || []),
    ...(pnlLosses?.waiting.tags || []),
  ] as PnlLossTag[];

  const barTimesWhichAffectBarWidthsArr = tagsAffectingBarWidthsArr.reduce(
    (acc, { downtimePlusDependentSpeedLossMs }) => [
      ...acc,
      downtimePlusDependentSpeedLossMs,
    ],
    [
      ...(hideProducingLosses
        ? []
        : doesCustomerShowProducing
          ? [minorStopTime || 0, slowdownLossTime || 0]
          : // for customers hiding producing (via CATEGORIES_TO_HIDE_PER_CUSTOMER) but still showing minor stop and slowdown loss
            [
              unknownIndependentMinorStopLossMs || 0,
              unknownIndependentSlowdownLossMs || 0,
            ]),
      unknownDowntimeLossMs || 0,
      hideProducingLosses ? 0 : qualityLossTime || 0,
    ] as number[]
  );

  const biggestBarTime = Math.max(
    ...barTimesWhichAffectBarWidthsArr.map(time => Math.abs(time)) // negative times get positive widths
  );
  const numProcesses = processIds.length;

  const rangeTime = (rangeLt - range.ge) * numProcesses;

  const totalTime = doesCustomerShowProducing ? productionTime || 0 : rangeTime;

  const { totalProducingTime, effectiveProducingTimeTopDownOrBottomUp } =
    getTotalProducingTimeForProducingLosses({
      dataDumpWithProducingLosses,
      doesCustomerShowProducing:
        doesCustomerShowProducing && !hideProducingLosses,
      topDownOrBottomUp,
      producingTimeFromDataDumpAggregate,
    });
  const producingBarTimeToUse = hideProducingLosses
    ? (totalProducingTime ?? 0)
    : effectiveProducingTimeTopDownOrBottomUp;
  const downtimeLossMs_or_downtimePlusDependentSpeedLossMs: keyof PnlLoss =
    hideProducingLosses || !includeDependentLosses
      ? "downtimeLossMs"
      : "downtimePlusDependentSpeedLossMs";
  const tagsPercentsObject =
    rangeTime === 0
      ? getTagsObjectBase(doesCustomerShowProducing)
      : {
          ...getTagsObjectBase(doesCustomerShowProducing),
          producing: {
            categoryTime: totalProducingTime,
            categoryPercent:
              totalTime === 0 ? 0 : totalProducingTime / totalTime,
            bars: [
              {
                title: getCategoryOrSubCategoryTitle(
                  ckey,
                  hideProducingLosses
                    ? "Producing"
                    : PRODUCING_CHART_TITLES.effectiveProducingTime
                ),
                category: "producing",
                count: null,
                color: PNL_COLORS.producing,
                time: producingBarTimeToUse || 0,
                percent:
                  totalTime === 0
                    ? 0
                    : (producingBarTimeToUse || 0) / totalTime,
                percentWidth: (producingBarTimeToUse || 0) / biggestBarTime,
                outputLoss: pnlLosses.producing?.outputLoss,
                numberOfEvents: pnlLosses.producing?.numberOfEvents,
                averageDurationMs: pnlLosses.producing?.averageDurationMs,
              },
              ...(doesCustomerShowProducing && !hideProducingLosses
                ? [
                    {
                      title: getCategoryOrSubCategoryTitle(
                        ckey,
                        hasWipIncrease
                          ? PRODUCING_CHART_TITLES.qualityLossWipIncrease
                          : PRODUCING_CHART_TITLES.qualityLoss
                      ),
                      category: "producing",
                      count: null,
                      color:
                        (qualityLossTime || 0) < 0
                          ? PNL_COLORS.speedGain
                          : PNL_COLORS.qualityLoss,
                      time: qualityLossTime || 0,
                      percent:
                        totalTime === 0
                          ? 0
                          : (qualityLossTime || 0) / totalTime,
                      percentWidth:
                        biggestBarTime === 0
                          ? 0
                          : (qualityLossTime || 0) / biggestBarTime,
                    },
                    {
                      title: getCategoryOrSubCategoryTitle(
                        ckey,
                        PRODUCING_CHART_TITLES.slowdownLoss
                      ),
                      category: "producing",
                      count: null,
                      color:
                        (slowdownLossTime || 0) < 0
                          ? PNL_COLORS.speedGain
                          : PNL_COLORS.qualityLoss,
                      time: slowdownLossTime || 0,
                      percent:
                        totalTime === 0
                          ? 0
                          : (slowdownLossTime || 0) / totalTime,
                      percentWidth:
                        biggestBarTime === 0
                          ? 0
                          : (slowdownLossTime || 0) / biggestBarTime,
                    },
                    {
                      title: getCategoryOrSubCategoryTitle(
                        ckey,
                        PRODUCING_CHART_TITLES.minorStopLoss
                      ),
                      category: "producing",
                      count: null,
                      color:
                        (minorStopTime || 0) < 0
                          ? PNL_COLORS.speedGain
                          : PNL_COLORS.qualityLoss,
                      time: minorStopTime || 0,
                      percent:
                        totalTime === 0 ? 0 : (minorStopTime || 0) / totalTime,
                      percentWidth:
                        biggestBarTime === 0
                          ? 0
                          : (minorStopTime || 0) / biggestBarTime,
                      numberOfEvents:
                        pnlLosses.producing?.minorStops.numberOfEvents,
                      averageDurationMs:
                        pnlLosses.producing?.minorStops.averageDurationMs,
                    },
                  ]
                : []),
            ],
          },
          planned: {
            categoryTime:
              pnlLosses?.planned?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0,
            categoryPercent:
              (pnlLosses?.planned?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0) / totalTime,
            outputLoss: pnlLosses.planned.outputLoss,
            numberOfEvents: pnlLosses.planned.numberOfEvents,
            averageDurationMs: pnlLosses.planned.averageDurationMs,

            bars: mapTagsToSortedBars({
              pnlLosses,
              category: "planned",
              totalTime,
              biggestBarTime,
              downtimeLossMs_or_downtimePlusDependentSpeedLossMs,
            }),
          },
          unplanned: {
            categoryTime:
              pnlLosses?.unplanned?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0,
            categoryPercent:
              (pnlLosses?.unplanned?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0) / totalTime,
            numberOfEvents: pnlLosses.unplanned.numberOfEvents,
            averageDurationMs: pnlLosses.unplanned.averageDurationMs,

            bars: mapTagsToSortedBars({
              pnlLosses,
              category: "unplanned",
              totalTime,
              biggestBarTime,
              downtimeLossMs_or_downtimePlusDependentSpeedLossMs,
            }),
          },
          waiting: {
            categoryTime:
              pnlLosses?.waiting?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0,
            categoryPercent:
              (pnlLosses?.waiting?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0) / totalTime,
            outputLoss: pnlLosses.waiting.outputLoss,
            numberOfEvents: pnlLosses.waiting.numberOfEvents,
            averageDurationMs: pnlLosses.waiting.averageDurationMs,

            bars: mapTagsToSortedBars({
              pnlLosses,
              category: "waiting",
              totalTime,
              biggestBarTime,
              downtimeLossMs_or_downtimePlusDependentSpeedLossMs,
            }),
          },
          unknown: {
            categoryTime: totalUnknownTime,
            categoryPercent: totalUnknownTime / totalTime,
            outputLoss: pnlLosses.unknown.outputLoss,
            numberOfEvents: pnlLosses.unknown.numberOfEvents,
            averageDurationMs: pnlLosses.unknown.averageDurationMs,

            bars: [
              {
                title: "Unknown Stop Time",
                category: "unknown",
                count: null,
                color: PNL_COLORS.unknown,
                time: doesCustomerShowProducing
                  ? totalUnknownTime
                  : unknownDowntimePlusDependentSpeedLossMs || 0,
                percent:
                  (doesCustomerShowProducing
                    ? totalUnknownTime
                    : unknownDowntimePlusDependentSpeedLossMs || 0) / totalTime,
                percentWidth:
                  (doesCustomerShowProducing
                    ? totalUnknownTime
                    : unknownDowntimePlusDependentSpeedLossMs || 0) /
                  biggestBarTime,
                outputLoss: pnlLosses.unknown.outputLoss,
                numberOfEvents: pnlLosses.unknown.numberOfEvents,
                averageDurationMs: pnlLosses.unknown.averageDurationMs,
              },
              ...(doesCustomerShowProducing
                ? []
                : [
                    {
                      title: PRODUCING_CHART_TITLES.slowdownLoss,
                      category: "qualityLoss",
                      count: null,
                      color: PNL_COLORS.unknown,
                      time: unknownIndependentSlowdownLossMs || 0,
                      percent:
                        (unknownIndependentSlowdownLossMs || 0) / totalTime,
                      percentWidth:
                        (unknownIndependentSlowdownLossMs || 0) /
                        biggestBarTime,
                    },
                    {
                      title: PRODUCING_CHART_TITLES.unknownMinorStopTime,
                      category: "qualityLoss",
                      count: null,
                      color: PNL_COLORS.unknown,
                      time: unknownIndependentMinorStopLossMs || 0,
                      percent:
                        (unknownIndependentMinorStopLossMs || 0) / totalTime,
                      percentWidth:
                        (unknownIndependentMinorStopLossMs || 0) /
                        biggestBarTime,
                    },
                  ]),
            ].sort((a, b) => (a.percent > b.percent ? -1 : 1)),
          },
          notScheduled: {
            categoryTime:
              pnlLosses?.notScheduled?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0,
            categoryPercent:
              (pnlLosses?.notScheduled?.[
                downtimeLossMs_or_downtimePlusDependentSpeedLossMs
              ] || 0) / totalTime,
            outputLoss: pnlLosses.notScheduled.outputLoss,
            numberOfEvents: pnlLosses.notScheduled.numberOfEvents,
            averageDurationMs: pnlLosses.notScheduled.averageDurationMs,

            bars: mapTagsToSortedBars({
              pnlLosses,
              category: "notScheduled",
              totalTime,
              biggestBarTime,
              downtimeLossMs_or_downtimePlusDependentSpeedLossMs,
            }),
          },
          uncollected: {
            categoryTime: uncollectedTime || 0,
            categoryPercent: (uncollectedTime || 0) / totalTime,
            bars: [
              {
                title: "Uncollected",
                category: "uncollected",
                count: null,
                color: PNL_COLORS.uncollected,
                time: uncollectedTime || 0,
                percent: (uncollectedTime || 0) / totalTime,
                percentWidth: (uncollectedTime || 0) / biggestBarTime,
              },
            ],
          },
          ...(!doesCustomerShowProducing
            ? {
                quality: {
                  categoryTime: qualityLossTime || 0,
                  categoryPercent: (qualityLossTime || 0) / totalTime,
                  bars: [
                    {
                      title: PRODUCING_CHART_TITLES.qualityLoss,
                      category: "qualityLoss",
                      count: null,
                      color:
                        (qualityLossTime || 0) < 0
                          ? PNL_COLORS.speedGain
                          : PNL_COLORS.qualityLoss,
                      time: qualityLossTime || 0,
                      percent: (qualityLossTime || 0) / totalTime,
                      percentWidth: (qualityLossTime || 0) / biggestBarTime,
                    },
                  ],
                },
              }
            : {}),
        };

  const sortedPnlCategories = doesCustomerShowProducing
    ? SORTED_STANDARD_PNL_CATEGORIES
    : [QUALITY_LOSS_CATEGORY.category, ...SORTED_STANDARD_PNL_CATEGORIES];

  return sortedPnlCategories.map(category => {
    const { categoryTime, categoryPercent, bars } =
      tagsPercentsObject[category];

    return {
      title: PnlCategoryLabelsEnum[category],
      time: categoryTime,
      count: null,
      percent: categoryPercent,
      bars: bars,
    };
  });
};

function mapTagsToSortedBars({
  pnlLosses,
  category,
  totalTime,
  biggestBarTime,
  downtimeLossMs_or_downtimePlusDependentSpeedLossMs,
}: {
  pnlLosses: PnlLosses;
  category: string;
  totalTime: number;
  biggestBarTime: number;
  downtimeLossMs_or_downtimePlusDependentSpeedLossMs: keyof PnlLoss;
}) {
  return pnlLosses[category]?.tags
    .map(
      ({
        name,
        path,
        // downtimePlusDependentSpeedLossMs,
        // downtimeLossMs,
        // downtimeDependentSpeedLossMs,
        outputLoss,
        numberOfEvents,
        averageDurationMs,
        ...tag
      }) => ({
        title: name,
        category,
        count: null,
        color: PNL_COLORS[category],
        time: tag[downtimeLossMs_or_downtimePlusDependentSpeedLossMs] || 0,
        percent:
          (tag[downtimeLossMs_or_downtimePlusDependentSpeedLossMs] || 0) /
          totalTime,
        percentWidth:
          (tag[downtimeLossMs_or_downtimePlusDependentSpeedLossMs] || 0) /
          biggestBarTime,
        outputLoss,
        numberOfEvents,
        averageDurationMs,
      })
    )
    .sort((a, b) => (a.percent > b.percent ? -1 : 1));
}

export function getTotalProducingTimeForProducingLosses({
  dataDumpWithProducingLosses,
  doesCustomerShowProducing,
  topDownOrBottomUp,
  producingTimeFromDataDumpAggregate,
}: {
  dataDumpWithProducingLosses: DataDumpAggregateType;
  doesCustomerShowProducing: boolean;
  topDownOrBottomUp: TopDownOrBottomUp;
  producingTimeFromDataDumpAggregate: boolean;
}): {
  totalProducingTime: number;
  effectiveProducingTimeTopDownOrBottomUp: number;
} {
  const {
    effectiveProducingTimeBottomUp,
    effectiveProducingTimeTopDown,
    effectiveProducingTime,
    slowdownLossTimeReal,
    minorStopTime,
    pnlLosses,
    producingTime,
  } = dataDumpWithProducingLosses;

  const qualityLossTime = pnlLosses?.quality.qualityLossMs;
  const slowdownLossTime = slowdownLossTimeReal;

  const effectiveProducingTimeTopDownOrBottomUp =
    topDownOrBottomUp === "bottom-up"
      ? effectiveProducingTimeBottomUp
      : topDownOrBottomUp === "top-down"
        ? effectiveProducingTimeTopDown
        : effectiveProducingTime ||
          effectiveProducingTimeTopDown ||
          effectiveProducingTimeBottomUp;

  let totalProducingTime = effectiveProducingTimeTopDownOrBottomUp || 0;
  if (producingTimeFromDataDumpAggregate) {
    // This is a feature flag that allows us to use the producing time
    // directly from the data dump aggregate. It doesn't matter if they
    // do or don't show producing. This value is always correct and will
    // be consistent with reports and data dumps elsewhere.
    totalProducingTime = producingTime || 0;
  } else if (doesCustomerShowProducing) {
    // This preserves legacy behavior for customers that show producing.
    // Getting the producing time from the data dump aggregate will
    // likely be preferred.
    totalProducingTime +=
      (qualityLossTime || 0) + (slowdownLossTime || 0) + (minorStopTime || 0);
  }

  return {
    totalProducingTime,
    effectiveProducingTimeTopDownOrBottomUp:
      effectiveProducingTimeTopDownOrBottomUp || 0,
  };
}

function getCategoryOrSubCategoryTitle(ckey: string, originalTitle: string) {
  return (
    CUSTOM_PNL_CHART_HEADER_TITLES?.[ckey]?.[originalTitle] ?? originalTitle
  );
}
